// Feature flags — committed constants (replaces PostHog-backed flags, 2026-07-06).
//
// CLAUDE.md prescribes flag-gating whenever a frontend PR lands ahead of its
// backend ("ship behind a feature flag, flip it once the backend is live").
// That mechanism now lives here: a flag's live value is the constant below.
//
// Releasing a gated feature = flip its value to `true` here and merge (one-line
// PR → Vercel auto-deploy). Kill-switch = flip back to `false` and merge, or
// revert the deploy (~2 min). We traded PostHog's no-deploy remote flip for
// zero third-party dependency and no orphaned analytics account — see the
// PostHog removal in this same change.
//
// This is the prod source of truth. `useFeatureFlag(key, fallback)` reads it;
// an unknown key falls back to the caller's `fallback`, preserving the old
// contract exactly.
export const FEATURE_FLAGS: Record<string, boolean> = {
  // Manual (FreeCAD-style) CAD workbench — live on prod (launched 2026-07-05).
  'manual-cad': true,
  // Floating feature-tree windows — the permanent SolidWorks-style pattern:
  // every domain's parameters live in tree items that open floating windows,
  // never as raw fields filling the tree. Kept as a flag (rather than deleting
  // the gate) purely as a fast kill-switch — flip back to `false` to fall back
  // to each domain's legacy single-pane rendering if this ever regresses.
  'feature-tree-windows': true,
  // Scan-to-CAD (mesh → STEP reverse-engineering) — launched 2026-07-07.
  // Full chain live + verified end-to-end (staging scan → real STEP): browser →
  // `scan-to-cad` edge fn (auth + 30-token billing + signed URL, rule C) →
  // `recon-microservice` on Fly. Kill-switch: flip back to `false` and merge.
  'scan-to-cad': true,
  // Mesh-to-STEP (ANALYTIC-tier mesh → STEP) — LAUNCHED 2026-07-18. Same two-hop
  // shape as scan-to-cad: browser → `mesh-to-step` edge fn (auth + 30-token
  // billing + signed URL, rule C) → `magneto-recon` on Fly. Full chain deployed
  // + E2E-verified (prismatic → grade B, 6 cylinders). Honest grader: analytic-
  // friendly parts grade A/B/C, others honestly D/F ("review required").
  // Kill-switch: flip back to `false` and merge.
  'mesh-to-step': true,
};

// Look up a flag's committed value; `undefined` if the key isn't declared.
export function getFeatureFlag(key: string): boolean | undefined {
  return Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, key)
    ? FEATURE_FLAGS[key]
    : undefined;
}
